import React, { useCallback, useEffect, useRef, useState } from "react"
import { faClose, faLocationDot, faWarning } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import { CountElements } from "../../../components/CountElements/CountElements"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useGetServicePointOpenOrders } from "../../../hooks/ServicePoints/useGetServicePointOpenOrders"
import { orderMemory } from "../../../Objects/Memory/Memory"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { ServicePoints } from "../../../Objects/ServicePoint/ServicePoints"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"
import { OrderPupop } from "../Order/OrderPupop"
import { OrderPupopClose } from "../Order/OrderPupopClose"

interface ServicePointOrdersPopupProps {
  servicePointData: ServicePoints
  onClose: () => void
}

export const ServicePointOrdersPopup: React.FC<ServicePointOrdersPopupProps> = ({ servicePointData, onClose }) => {
  const { t } = useTranslation("servicePoints")

  const { t: tOrder } = useTranslation("order")

  const { handleGetServicePointOpenOrders } = useGetServicePointOpenOrders()

  const handleGetServicePointOpenOrdersRef = useRef(handleGetServicePointOpenOrders)

  const [isLoading, setIsLoading] = useState(false)

  const [orders, setOrders] = useState<OrderEntity[]>([])

  const [selectedOrder, setSelectedOrder] = useState<OrderEntity | null>(null)

  useEffect(() => {
    handleGetServicePointOpenOrdersRef.current = handleGetServicePointOpenOrders
  }, [handleGetServicePointOpenOrders])

  const handleFetchOrders = useCallback(async () => {
    setIsLoading(true)
    setOrders([])

    try {
      await handleGetServicePointOpenOrdersRef.current(servicePointData.id, setOrders)
    } finally {
      setIsLoading(false)
    }
  }, [servicePointData.id])

  useEffect(() => {
    void handleFetchOrders()
  }, [handleFetchOrders])

  return (
    <>
      <ModalPortal>
        <div className={defaultStyle.overlay}>
          <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
            <div className={defaultStyle.alert}>
              <TitlePageResponsiveIconSmall
                iconColor={THEME.colors.iconBGSecondaryColor}
                title={`${t("label_service_point_relations_title")} - ${servicePointData.name}`}
                icon={faLocationDot}
                children={
                  <ButtonIcon
                    icon={faClose}
                    size="small"
                    typeButton="button"
                    onClick={onClose}
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}

                  descriptionKey="interactionDescriptions:pages.fluxo.servicePoints.servicePointOrdersPopup.button.onClose" />
                }
              />

              <GetSpace top={10} />
              {isLoading ? (
                <span className="text-xs text-slate-500">{t("label_service_point_relations_loading")}</span>
              ) : orders.length === 0 ? (
                <TitlePageResponsiveIconSmall
                  boxShadow="none"
                  icon={faWarning}
                  subtitle={t("label_service_point_relations_empty")}
                />
              ) : (
                <div className="max-h-[50vh] overflow-auto pr-1">
                  <CountElements
                    count={orders.length}
                    title={t("label_service_point_orders_count")}
                    animationTypes={AnimationTypes.slideUpIn}
                    animationDuration="2s"
                  />

                  {orders.map((order, index) => (
                    <Animate key={order.id} type={AnimationTypes.fadeInTraslate} duration={`${0.4 + index * 0.25}s`}>
                      <button
                        type="button"
                        onClick={() => setSelectedOrder(order)}
                        className="mb-3 w-full rounded-2xl border border-orange-200 bg-gradient-to-r from-white to-orange-50 p-1 text-left transition-all hover:scale-[1.01] hover:shadow-md"

                  data-interaction-description-key="interactionDescriptions:pages.fluxo.servicePoints.servicePointOrdersPopup.button.setSelectedOrderOrder">
                        <div className="rounded-2xl border border-orange-100 bg-white">
                          {orderMemory.getCard(order, index).children}
                        </div>
                      </button>
                    </Animate>
                  ))}
                </div>
              )}
            </div>
          </Animate>
        </div>
      </ModalPortal>

      {selectedOrder &&
        (selectedOrder.orderPaymentEntity?.isClosed() ? (
          <OrderPupopClose order={selectedOrder} onClonse={() => setSelectedOrder(null)} onSave={handleFetchOrders} />
        ) : (
          <OrderPupop
            order={selectedOrder}
            title={tOrder("title_popup_edite")}
            onClonse={() => setSelectedOrder(null)}
            onSave={() => {
              setSelectedOrder(null)
              void handleFetchOrders()
            }}
          />
        ))}
    </>
  )
}
